import { useEffect, useState, useContext } from "react";
import Content from "../../../../../layout/Content";
import billingApi from "@serie3/freelancesplaceapi/api/finance/out";
import TableRowBills from "../../TableRowBills";
import BillView from "../../modals/BillView";
import Billout from "../../BillOut";
import BillIn from "../../BillIn";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faArrowDownWideShort, faArrowUpWideShort } from "@fortawesome/free-solid-svg-icons";
import { btn, btnHoverStyle } from "../../../../../layout/Buttons/buttonStyles";
import { useNavigate } from "react-router-dom";
import FinanceHeader from "../../FinanceHeader";
import listHandler from "@serie3/common/Domain/listHandler";
import getSettings from "@serie3/common/config/settings";
import PaymentModal from "../../../../../layout/Payment/PaymentModal";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import formatAmount from "../../formatAmount";
import FinanceCsvImport from "../../FinanceCsvImport";

const YEAR_KEY = "finance_year";

const Index = ({ type, direction, titleText }) => {
  const { settings: userSettings } = useContext(SettingsContext);
  const locale = userSettings?.locale;
  const fmt = (n) => formatAmount(n, locale);

  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [bill, setBill] = useState();
  const [showRowError, setShowRowError] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [settings, setSettings] = useState();
  const [showPaidRow, setShowPaidRow] = useState(false);
  const [sortAsc, setSortAsc] = useState(false);

  const [year, setYear] = useState(
    parseInt(localStorage.getItem(YEAR_KEY) || new Date().getFullYear(), 10)
  );

  const updateYear = (y) => {
    localStorage.setItem(YEAR_KEY, y);
    setYear(y);
  };

  const navigate = useNavigate();

  const loadData = async (selectedYear) => {
    const response = await billingApi.getAll(type, direction, selectedYear);
    setData(response.data.finanzvorgang || []);
  };


  const sortedData = [...data].sort((a, b) =>
    sortAsc
      ? new Date(a.datum || 0) - new Date(b.datum || 0)
      : new Date(b.datum || 0) - new Date(a.datum || 0)
  );

  const createNew = async () => {
    const id = await billingApi.createNew(type, direction, setShowPayModal);
    if (typeof id !== "undefined" && typeof id === "number") {
      navigate("/finance/" + direction + "/" + type + "/edit/" + id);
    }
  };

  useEffect(() => {
    setSettings(getSettings());
    setData([]);
    loadData(year);
  }, [type, direction, year]);

  const NewButton = () => (
    <>
      <style>{btnHoverStyle}</style>
      <FinanceCsvImport direction={direction} onImported={() => loadData(year)} />
      <span style={{ display: "inline-block", width: "10px" }} />
      <button className="btn-modern" style={{ ...btn.base, ...btn.primary }} onClick={createNew}>
        <FontAwesomeIcon icon={faAdd} /> New
      </button>
    </>
  );

  const Title = () => <span>{titleText}</span>;

  const setTheList = (entities) => {
    setData(entities);
  };

  const onMarkPaid = (index) => {
    setData((prev) => prev.map((b, i) => i === index ? { ...b, geldeingang: true } : b));
  };

  const deleteFromList = async (index, apiCall) => {
    listHandler.deleteFromList(
      index,
      apiCall,
      setTheList,
      sortedData,
      setShowRowError
    );
  };

  const ShowType = ({ bill, direction }) => {
    if (direction === "out") {
      return <Billout bill={bill} />;
    }
    if (direction === "in") {
      return <BillIn bill={bill} type={type} />;
    }
  };

  return (
    <>
      <BillView show={show} bill={bill} setShow={setShow} direction={direction}>
        <ShowType direction={direction} bill={bill} />
      </BillView>
      {settings && (
        <PaymentModal
          show={showPayModal}
          setShow={setShowPayModal}
          priceTableId={settings.pricingTables.billing}
        />
      )}
      <FinanceHeader direction={direction} year={year} setYear={updateYear} />
      <Content Title={Title} AdditionalButtons={NewButton}>
        <div style={{ marginBottom: "12px", display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => setSortAsc((v) => !v)}
            style={{
              display: "inline-flex", alignItems: "center", gap: "6px",
              padding: "5px 10px",
              background: "#f5f5f5", border: "none", borderRadius: "8px",
              fontWeight: 600, fontSize: "0.8rem", color: "#616161", cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={sortAsc ? faArrowUpWideShort : faArrowDownWideShort} style={{ fontSize: "0.75rem" }} />
            {sortAsc ? "Oldest first" : "Newest first"}
          </button>
        </div>
        <div style={{ overflowX: "auto", minHeight: "70vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Bill No.", "Customer", "Status", "Net", "Tax", "Gross", ""].map((h, i) => (
                  <th key={i} style={{
                    padding: "10px 12px",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "#9e9e9e",
                    background: "#f8f9fb",
                    borderBottom: "1px solid #f0f0f0",
                    whiteSpace: "nowrap",
                    textAlign: i >= 3 && i <= 5 ? "right" : "left",
                    width: i === 6 ? "40px" : undefined,
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData && sortedData.length > 0
                ? (() => {
                    // Pre-compute per-month totals
                    const monthTotals = {};
                    sortedData.forEach((bill) => {
                      const key = bill.datum
                        ? new Date(bill.datum).toLocaleDateString("en-US", { month: "long", year: "numeric" })
                        : "Unknown";
                      if (!monthTotals[key]) monthTotals[key] = { netto: 0, brutto: 0, count: 0 };
                      monthTotals[key].netto  += bill.netto  || 0;
                      monthTotals[key].brutto += bill.brutto || 0;
                      monthTotals[key].count  += 1;
                    });

                    const rows = [];
                    let lastMonth = null;
                    const grossColor = direction === "in" ? "#c62828" : "#2e7d32";
                    sortedData.forEach((bill, index) => {
                      const monthKey = bill.datum
                        ? new Date(bill.datum).toLocaleDateString("en-US", { month: "long", year: "numeric" })
                        : "Unknown";
                      if (monthKey !== lastMonth) {
                        lastMonth = monthKey;
                        const t = monthTotals[monthKey];
                        const tax = t.brutto - t.netto;
                        rows.push(
                          <tr key={`sep-${monthKey}`} style={{
                            background: "linear-gradient(90deg, #e8f0fe 0%, #f0f4ff 100%)",
                            borderTop: index > 0 ? "2px solid #c5d3f0" : "none",
                            borderBottom: "2px solid #c5d3f0",
                          }}>
                            <td colSpan={3} style={{ padding: "9px 12px" }}>
                              <span style={{
                                fontSize: "0.72rem", fontWeight: 900,
                                textTransform: "uppercase", letterSpacing: "0.12em",
                                color: "#1a73e8",
                              }}>{monthKey} <span style={{ fontWeight: 600, opacity: 0.7 }}>({t.count})</span></span>
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right", fontSize: "0.78rem", fontWeight: 700, color: "#424242", fontVariantNumeric: "tabular-nums" }}>
                              {fmt(t.netto)} €
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right", fontSize: "0.78rem", fontWeight: 700, color: "#7986cb", fontVariantNumeric: "tabular-nums" }}>
                              {fmt(tax)} €
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "right", fontSize: "0.88rem", fontWeight: 900, color: grossColor, fontVariantNumeric: "tabular-nums" }}>
                              {fmt(t.brutto)} €
                            </td>
                            <td style={{ background: "transparent", padding: "9px 12px" }} />
                          </tr>
                        );
                      }
                      rows.push(
                        <TableRowBills
                          key={index}
                          index={index}
                          bill={bill}
                          setShow={setShow}
                          setBill={setBill}
                          direction={direction}
                          type={type}
                          deleteFunction={deleteFromList}
                          onMarkPaid={onMarkPaid}
                        />
                      );
                    });
                    return rows;
                  })()
                : (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "48px 0", color: "#bdbdbd", fontSize: "0.92rem" }}>
                      No bills found for {year}.
                    </td>
                  </tr>
                )}
            </tbody>
            {sortedData && sortedData.length > 0 && (() => {
              const isIncoming = direction === "in";
              const totalNetto = data.reduce((sum, b) => sum + (b.netto || 0), 0);
              const totalBrutto = data.reduce((sum, b) => sum + (b.brutto || 0), 0);
              const totalTax = totalBrutto - totalNetto;
              const grossColor = isIncoming ? "#c62828" : "#2e7d32";
              const paidBills = data.filter((b) => b.geldeingang);
              const paidNetto = paidBills.reduce((sum, b) => sum + (b.netto || 0), 0);
              const paidBrutto = paidBills.reduce((sum, b) => sum + (b.brutto || 0), 0);
              const paidTax = paidBrutto - paidNetto;
              return (
                <tfoot>
                  <tr style={{ background: "#eef0f4" }}>
                    <td colSpan={3} style={{ padding: "14px 12px", fontSize: "0.82rem", fontWeight: 700, color: "#424242", boxShadow: "inset 0 2px 0 #9e9e9e" }}>
                      Total — {data.length} bill{data.length !== 1 ? "s" : ""}
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "right", fontSize: "0.88rem", fontWeight: 600, color: "#424242", fontVariantNumeric: "tabular-nums", boxShadow: "inset 0 2px 0 #9e9e9e" }}>
                      {fmt(totalNetto)} €
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "right", fontSize: "0.88rem", fontWeight: 600, color: "#9e9e9e", fontVariantNumeric: "tabular-nums", boxShadow: "inset 0 2px 0 #9e9e9e" }}>
                      {fmt(totalTax)} €
                    </td>
                    <td style={{ padding: "14px 12px", textAlign: "right", fontSize: "0.95rem", fontWeight: 800, color: grossColor, fontVariantNumeric: "tabular-nums", boxShadow: "inset 0 2px 0 #9e9e9e" }}>
                      {fmt(totalBrutto)} €
                    </td>
                    <td style={{ background: "#eef0f4", padding: "14px 8px", textAlign: "right", boxShadow: "inset 0 2px 0 #9e9e9e" }}>
                      <button
                        onClick={() => setShowPaidRow((v) => !v)}
                        title={showPaidRow ? "Hide paid breakdown" : "Show paid breakdown"}
                        style={{
                          background: showPaidRow ? "#c8e6c9" : "#e0e4ee",
                          border: "none", borderRadius: "6px",
                          width: "22px", height: "22px",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", color: showPaidRow ? "#2e7d32" : "#757575",
                          fontSize: "0.7rem", transition: "background 0.15s, color 0.15s",
                          padding: 0, flexShrink: 0,
                        }}
                      >
                        {showPaidRow ? "▲" : "▼"}
                      </button>
                    </td>
                  </tr>
                  {showPaidRow && <tr style={{ background: "#f5fbf5", borderTop: "1px solid #c8e6c9" }}>
                    <td colSpan={3} style={{ padding: "9px 12px", fontSize: "0.78rem", fontWeight: 600, color: "#388e3c" }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: "5px",
                      }}>
                        <span style={{
                          width: "7px", height: "7px", borderRadius: "50%",
                          background: "#43a047", display: "inline-block", flexShrink: 0,
                        }} />
                        Paid — {paidBills.length} of {data.length}
                      </span>
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right", fontSize: "0.82rem", fontWeight: 600, color: "#388e3c", fontVariantNumeric: "tabular-nums" }}>
                      {fmt(paidNetto)} €
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right", fontSize: "0.82rem", fontWeight: 500, color: "#81c784", fontVariantNumeric: "tabular-nums" }}>
                      {fmt(paidTax)} €
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right", fontSize: "0.88rem", fontWeight: 700, color: "#2e7d32", fontVariantNumeric: "tabular-nums" }}>
                      {fmt(paidBrutto)} €
                    </td>
                    <td style={{ background: "#f5fbf5" }} />
                  </tr>}
                </tfoot>
              );
            })()}
          </table>
        </div>
      </Content>
    </>
  );
};

export default Index;
